import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { 
  Settings,
  Shield,
  Trash2,
  AlertTriangle,
  Save,
  RefreshCw,
  Shuffle,
  Clock,
  Copy,
  Eye,
  EyeOff,
  Calendar,
  Trophy
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface League {
  id: string;
  name: string;
  owner_id: string;
  is_private: boolean;
  password_hash?: string;
  draft_at?: string;
  starters_count: number;
  bench_count: number;
  description?: string;
  max_teams?: number;
  status?: string;
}

interface LeagueSettingsProps {
  league: League;
  isOwner: boolean;
  participantCount: number;
  onLeagueUpdate: (updatedLeague: Partial<League>) => void;
}

export function LeagueSettings({ league, isOwner, participantCount, onLeagueUpdate }: LeagueSettingsProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [showPasswordField, setShowPasswordField] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [draftCountdown, setDraftCountdown] = useState<string>('');

  // Settings form data
  const [generalSettings, setGeneralSettings] = useState({
    name: league.name,
    description: league.description || '',
    maxTeams: league.max_teams || 12
  });

  const [draftSettings, setDraftSettings] = useState({
    draftType: 'snake',
    draftAt: league.draft_at || '',
    pickDuration: 60,
    autoPick: true
  });

  const [rosterSettings, setRosterSettings] = useState({
    starters: league.starters_count,
    bench: league.bench_count,
    scoringPreset: 'balanced'
  });

  const [privacySettings, setPrivacySettings] = useState({
    isPrivate: league.is_private,
    password: ''
  });

  // Update countdown timer
  useEffect(() => {
    if (league.draft_at) {
      const interval = setInterval(() => {
        const now = new Date().getTime();
        const draftTime = new Date(league.draft_at!).getTime();
        const difference = draftTime - now;

        if (difference > 0) {
          const days = Math.floor(difference / (1000 * 60 * 60 * 24));
          const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
          const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));

          if (days > 0) {
            setDraftCountdown(`${days}d ${hours}h ${minutes}m`);
          } else {
            setDraftCountdown(`${hours}h ${minutes}m`);
          }
        } else {
          setDraftCountdown(league.status === 'live' ? 'Draft started!' : 'Awaiting start');
        }
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [league.draft_at, league.status]);

  // Handle form changes
  const handleGeneralChange = (field: string, value: any) => {
    setGeneralSettings(prev => ({ ...prev, [field]: value }));
    setHasUnsavedChanges(true);
  };

  const handleDraftChange = (field: string, value: any) => {
    setDraftSettings(prev => ({ ...prev, [field]: value }));
    setHasUnsavedChanges(true);
  };

  const handleRosterChange = (field: string, value: any) => {
    setRosterSettings(prev => ({ ...prev, [field]: value }));
    setHasUnsavedChanges(true);
  };

  const handlePrivacyChange = (field: string, value: any) => {
    setPrivacySettings(prev => ({ ...prev, [field]: value }));
    setHasUnsavedChanges(true);
  };

  // Save functions
  const saveGeneralSettings = async () => {
    if (!isOwner) return;

    try {
      setLoading(true);
      const { error } = await supabase
        .from('leagues')
        .update({
          name: generalSettings.name,
          description: generalSettings.description,
          max_teams: generalSettings.maxTeams
        })
        .eq('id', league.id);

      if (error) throw error;

      onLeagueUpdate({
        name: generalSettings.name,
        description: generalSettings.description,
        max_teams: generalSettings.maxTeams
      });

      toast({
        title: 'Settings Updated',
        description: 'General settings have been saved successfully.',
      });

      setHasUnsavedChanges(false);
    } catch (error: any) {
      console.error('Error updating general settings:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to update settings.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const saveDraftSettings = async () => {
    if (!isOwner) return;

    try {
      setLoading(true);
      const { error } = await supabase
        .from('leagues')
        .update({
          draft_at: draftSettings.draftAt || null
        })
        .eq('id', league.id);

      if (error) throw error;

      onLeagueUpdate({
        draft_at: draftSettings.draftAt
      });

      toast({
        title: 'Draft Settings Updated',
        description: 'Draft settings have been saved successfully.',
      });

      setHasUnsavedChanges(false);
    } catch (error: any) {
      console.error('Error updating draft settings:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to update draft settings.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const saveRosterSettings = async () => {
    if (!isOwner) return;

    try {
      setLoading(true);
      const { error } = await supabase
        .from('leagues')
        .update({
          starters_count: rosterSettings.starters,
          bench_count: rosterSettings.bench
        })
        .eq('id', league.id);

      if (error) throw error;

      onLeagueUpdate({
        starters_count: rosterSettings.starters,
        bench_count: rosterSettings.bench
      });

      toast({
        title: 'Roster Settings Updated',
        description: 'Roster settings have been saved successfully.',
      });

      setHasUnsavedChanges(false);
    } catch (error: any) {
      console.error('Error updating roster settings:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to update roster settings.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const savePrivacySettings = async () => {
    if (!isOwner) return;

    try {
      setLoading(true);
      
      // Hash password if provided
      let passwordHash = undefined;
      if (privacySettings.isPrivate && privacySettings.password) {
        // In a real app, you'd hash this properly. For now, we'll store it directly
        passwordHash = privacySettings.password;
      }

      const { error } = await supabase
        .from('leagues')
        .update({
          is_private: privacySettings.isPrivate,
          password_hash: privacySettings.isPrivate ? passwordHash : null
        })
        .eq('id', league.id);

      if (error) throw error;

      onLeagueUpdate({
        is_private: privacySettings.isPrivate,
        password_hash: passwordHash
      });

      toast({
        title: 'Privacy Settings Updated',
        description: 'Privacy settings have been saved successfully.',
      });

      setHasUnsavedChanges(false);
      setPrivacySettings(prev => ({ ...prev, password: '' }));
    } catch (error: any) {
      console.error('Error updating privacy settings:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to update privacy settings.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    const password = Array.from({ length: 8 }, () => chars.charAt(Math.floor(Math.random() * chars.length))).join('');
    setPrivacySettings(prev => ({ ...prev, password }));
    setHasUnsavedChanges(true);
  };

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({
        title: `${label} Copied!`,
        description: 'The text has been copied to your clipboard.',
      });
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const handleDeleteLeague = async () => {
    if (!isOwner || deleteConfirmText !== league.name) return;

    try {
      setLoading(true);
      const { error } = await supabase
        .from('leagues')
        .delete()
        .eq('id', league.id);

      if (error) throw error;

      toast({
        title: 'League Deleted',
        description: 'The league has been permanently deleted.',
      });

      // Redirect to leagues page
      window.location.href = '/leagues';
    } catch (error: any) {
      console.error('Error deleting league:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete league.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isOwner) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <Shield className="h-16 w-16 mx-auto text-muted-foreground/50 mb-4" />
              <h3 className="text-xl font-semibold mb-2">Owner Only</h3>
              <p className="text-muted-foreground">Only the league owner can edit settings.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const shareUrl = `${window.location.origin}/league/${league.id}`;
  const isDraftSoon = league.draft_at && new Date(league.draft_at).getTime() - Date.now() < 30 * 60 * 1000;

  return (
    <div className="space-y-6">
      {/* Unsaved Changes Banner */}
      {hasUnsavedChanges && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            You have unsaved changes. Make sure to save your settings before leaving this page.
          </AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Settings Forms */}
        <div className="lg:col-span-2 space-y-6">
          {/* General Settings */}
          <Card>
            <CardHeader>
              <CardTitle>General Settings</CardTitle>
              <CardDescription>Basic league information and configuration</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="league-name">League Name</Label>
                <Input
                  id="league-name"
                  value={generalSettings.name}
                  onChange={(e) => handleGeneralChange('name', e.target.value)}
                  placeholder="Enter league name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="league-description">Description</Label>
                <Textarea
                  id="league-description"
                  value={generalSettings.description}
                  onChange={(e) => handleGeneralChange('description', e.target.value)}
                  placeholder="Describe your league (optional)"
                  rows={3}
                />
              </div>

                <div className="space-y-2">
                  <Label htmlFor="max-players">Maximum Players</Label>
                  <Select
                    value={generalSettings.maxTeams.toString()}
                    onValueChange={(value) => handleGeneralChange('maxTeams', parseInt(value))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="4">4 Players</SelectItem>
                      <SelectItem value="6">6 Players</SelectItem>
                      <SelectItem value="8">8 Players</SelectItem>
                      <SelectItem value="10">10 Players</SelectItem>
                      <SelectItem value="12">12 Players</SelectItem>
                    </SelectContent>
                  </Select>
                  {participantCount >= generalSettings.maxTeams && (
                    <p className="text-sm text-muted-foreground">League is currently full</p>
                  )}
                </div>

              <div className="flex justify-end pt-4 border-t">
                <Button onClick={saveGeneralSettings} disabled={loading}>
                  <Save className="h-4 w-4 mr-2" />
                  Save General Settings
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Draft Settings */}
          <Card>
            <CardHeader>
              <CardTitle>Draft Settings</CardTitle>
              <CardDescription>Configure your league draft</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Draft Type</Label>
                <Select value={draftSettings.draftType} onValueChange={(value) => handleDraftChange('draftType', value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="snake">Snake Draft</SelectItem>
                  </SelectContent>
                </Select>
              </div>

                <div className="space-y-2">
                  <Label htmlFor="draft-date">Draft Date & Time</Label>
                  <Input
                    id="draft-date"
                    type="datetime-local"
                    value={draftSettings.draftAt ? new Date(draftSettings.draftAt).toISOString().slice(0, 16) : ''}
                    onChange={(e) => handleDraftChange('draftAt', e.target.value ? new Date(e.target.value).toISOString() : '')}
                  />
                </div>

              <div className="space-y-2">
                <Label>Draft Duration per Pick</Label>
                <Select
                  value={draftSettings.pickDuration.toString()}
                  onValueChange={(value) => handleDraftChange('pickDuration', parseInt(value))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="30">30 seconds</SelectItem>
                    <SelectItem value="60">60 seconds</SelectItem>
                    <SelectItem value="90">90 seconds</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="auto-pick"
                  checked={draftSettings.autoPick}
                  onCheckedChange={(checked) => handleDraftChange('autoPick', checked)}
                />
                <Label htmlFor="auto-pick">Auto-Pick on Timeout</Label>
              </div>

              {isDraftSoon && (
                <Alert>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    Draft settings lock 30 minutes before start time.
                  </AlertDescription>
                </Alert>
              )}

              <div className="flex justify-end pt-4 border-t">
                <Button onClick={saveDraftSettings} disabled={loading || isDraftSoon}>
                  <Save className="h-4 w-4 mr-2" />
                  Save Draft Settings
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Roster & Scoring */}
          <Card>
            <CardHeader>
              <CardTitle>Roster & Scoring</CardTitle>
              <CardDescription>Configure team rosters and scoring system</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="starters">Starters</Label>
                  <Input
                    id="starters"
                    type="number"
                    min="11"
                    max="15"
                    value={rosterSettings.starters}
                    onChange={(e) => handleRosterChange('starters', parseInt(e.target.value))}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="bench">Bench</Label>
                  <Input
                    id="bench"
                    type="number"
                    min="0"
                    max="15"
                    value={rosterSettings.bench}
                    onChange={(e) => handleRosterChange('bench', parseInt(e.target.value))}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Position Rules</Label>
                <p className="text-sm text-muted-foreground">1 GK required; others flexible.</p>
              </div>

              <div className="space-y-2">
                <Label>Scoring Preset</Label>
                <Select
                  value={rosterSettings.scoringPreset}
                  onValueChange={(value) => handleRosterChange('scoringPreset', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="balanced">Balanced (Offense + Defense)</SelectItem>
                    <SelectItem value="offense">Offense-heavy</SelectItem>
                    <SelectItem value="defense">Defense-aware</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="link" className="p-0 h-auto text-sm">
                  View preset details
                </Button>
              </div>

              <div className="flex justify-end pt-4 border-t">
                <Button onClick={saveRosterSettings} disabled={loading}>
                  <Save className="h-4 w-4 mr-2" />
                  Save Roster & Scoring
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Privacy Settings */}
          <Card>
            <CardHeader>
              <CardTitle>Privacy Settings</CardTitle>
              <CardDescription>Control who can join your league</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <Label>Visibility</Label>
                <RadioGroup
                  value={privacySettings.isPrivate ? 'private' : 'public'}
                  onValueChange={(value) => handlePrivacyChange('isPrivate', value === 'private')}
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="public" id="public" />
                    <Label htmlFor="public">Public - Anyone can join</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="private" id="private" />
                    <Label htmlFor="private">Private - Requires password</Label>
                  </div>
                </RadioGroup>
              </div>

              {privacySettings.isPrivate && (
                <div className="space-y-2">
                  <Label htmlFor="league-password">Password</Label>
                  <div className="flex gap-2">
                    <Input
                      id="league-password"
                      type={showPassword ? "text" : "password"}
                      value={privacySettings.password}
                      onChange={(e) => handlePrivacyChange('password', e.target.value)}
                      placeholder="Enter league password"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={generatePassword}
                    >
                      Generate
                    </Button>
                  </div>
                </div>
              )}

              <div className="flex justify-end pt-4 border-t">
                <Button onClick={savePrivacySettings} disabled={loading}>
                  <Save className="h-4 w-4 mr-2" />
                  Save Privacy Settings
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Danger Zone */}
          <Card className="border-destructive">
            <CardHeader>
              <CardTitle className="text-destructive">Danger Zone</CardTitle>
              <CardDescription>Irreversible actions</CardDescription>
            </CardHeader>
            <CardContent>
              <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
                <DialogTrigger asChild>
                  <Button variant="destructive">
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete League
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Delete League</DialogTitle>
                    <DialogDescription>
                      This action cannot be undone. This will permanently delete the league and all associated data.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="confirm-name">
                        Type the league name <strong>{league.name}</strong> to confirm:
                      </Label>
                      <Input
                        id="confirm-name"
                        value={deleteConfirmText}
                        onChange={(e) => setDeleteConfirmText(e.target.value)}
                        placeholder="Enter league name"
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
                      Cancel
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={handleDeleteLeague}
                      disabled={deleteConfirmText !== league.name || loading}
                    >
                      Delete League
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Context Cards */}
        <div className="space-y-6">
          {/* Draft Status */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Draft Status
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {league.draft_at ? (
                <>
                  <Badge variant="secondary">Upcoming</Badge>
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">
                      {new Date(league.draft_at).toLocaleDateString()} at{' '}
                      {new Date(league.draft_at).toLocaleTimeString()}
                    </p>
                    <p className="font-mono text-sm font-semibold">{draftCountdown}</p>
                  </div>
                  <div className="space-y-2">
                    <Button variant="outline" size="sm" className="w-full">
                      <Shuffle className="h-4 w-4 mr-2" />
                      Generate Draft Order
                    </Button>
                    <Button size="sm" className="w-full">
                      Open Draft Room
                    </Button>
                  </div>
                </>
              ) : (
                <div className="text-center py-4">
                  <p className="text-muted-foreground mb-2">No draft scheduled yet</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => document.getElementById('draft-date')?.focus()}
                  >
                    Schedule Draft
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Invites */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <RefreshCw className="h-5 w-5" />
                Invites
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Invite Link</Label>
                <div className="flex gap-2">
                  <Input value={shareUrl} readOnly className="font-mono text-xs" />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => copyToClipboard(shareUrl, 'Link')}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {league.is_private && league.password_hash && (
                <div className="space-y-2">
                  <Label>Password</Label>
                  <div className="flex gap-2">
                    <Input
                      value={league.password_hash}
                      type={showPassword ? "text" : "password"}
                      readOnly
                      className="font-mono text-xs"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => copyToClipboard(league.password_hash!, 'Password')}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}

              <p className="text-xs text-muted-foreground">
                Players can find this league in the directory{league.is_private ? '; private leagues require the password to join' : ''}.
              </p>
            </CardContent>
          </Card>

          {/* Key Dates */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Key Dates
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Draft Date</Label>
                <p className="text-sm">
                  {league.draft_at
                    ? new Date(league.draft_at).toLocaleDateString() + ' at ' + new Date(league.draft_at).toLocaleTimeString()
                    : 'Not scheduled'
                  }
                </p>
              </div>

              <div className="space-y-2">
                <Label>Trade Deadline</Label>
                <p className="text-sm text-muted-foreground">Not set</p>
              </div>

              <div className="space-y-2">
                <Label>Weekly Lock Time</Label>
                <p className="text-sm">At first match kickoff</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}